// The rules behind the built-in app connect form, kept out of the component so they can be
// tested directly (same split as ConnectionsSettings.logic.ts).
//
// This is the form where someone types the API keys for DIA, Entegra, Idefix and every
// marketplace connector after them. It is built entirely from the app's own `fields` list
// served by GET /api/morrow/apps, so these rules have to hold for any field set, not just the
// ones that exist today.

/** The field shape the catalog serves; a structural subset of AppField. */
export interface CredentialFieldRule {
  readonly key: string;
  readonly required: boolean;
  readonly type: "text" | "password" | "email";
}

/**
 * Is every required field filled in?
 *
 * Whitespace does not count as filled. Without that, pasting a stray space into a required box
 * enables Connect, the server trims it back to empty, and the person gets "Fill in: API key" for
 * a box that visibly has something in it.
 */
export function requiredFieldsFilled(
  fields: ReadonlyArray<CredentialFieldRule>,
  values: Readonly<Record<string, string>>,
): boolean {
  return fields.every((field) => !field.required || (values[field.key] ?? "").trim() !== "");
}

/** Can the Connect button fire? False while a connect attempt is in flight. */
export function canSubmitCredentials(input: {
  readonly fields: ReadonlyArray<CredentialFieldRule>;
  readonly values: Readonly<Record<string, string>>;
  readonly busy: boolean;
}): boolean {
  return !input.busy && requiredFieldsFilled(input.fields, input.values);
}

/**
 * Which required fields are still empty — so the form can name them instead of leaving a
 * disabled button with no explanation.
 */
export function missingRequiredFields(
  fields: ReadonlyArray<CredentialFieldRule>,
  values: Readonly<Record<string, string>>,
): readonly string[] {
  return fields
    .filter((field) => field.required && (values[field.key] ?? "").trim() === "")
    .map((field) => field.key);
}
